<template>
	<div class="mall-goods-card" @click="handleCardClick">
		<!-- 图片区域 -->
		<div class="card-image-wrap">
			<img class="card-image" :src="image" :alt="title" />
			<!-- 点击量标签 -->
			<div v-if="clickCount" class="click-count-tag">
				<div class="click-count-num">{{ formatClickCount }}</div>
				<div class="click-count-label">点击量</div>
			</div>
		</div>

		<!-- 内容区域 -->
		<div class="card-body">
			<div class="card-title" :title="title">{{ title }}</div>
			<div class="card-divider"></div>
			<div class="card-footer">
				<div class="price-section">
					<span v-if="isNegotiable" class="price negotiable">面议</span>
					<span v-else class="price">
						<span class="price-symbol">¥</span>
						{{ price }}
					</span>
				</div>
				<div class="action-section">
					<template v-if="isNegotiable">
						<div class="contact-trigger">
							<span class="contact-text">立即沟通 >></span>
							<!-- 悬浮显示的联系方式 -->
							<div v-if="contactPhone" class="contact-tooltip">联系方式：{{ contactPhone }}</div>
						</div>
					</template>
					<template v-else>
						<span class="sold-count">已售：{{ soldCount }}</span>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	/**
	 * @description 商城商品卡片（可复用）
	 * @property {String} image 商品图片地址
	 * @property {String} title 服务标题
	 * @property {String} price 价格，传入"面议"或具体金额如"20K"
	 * @property {Number|String} soldCount 已售数量
	 * @property {String} contactPhone 联系方式（面议时悬浮显示）
	 * @property {Number|String} clickCount 点击量（右上角标签）
	 */
	export default {
		name: 'MallGoodsCard',
		props: {
			image: {
				type: String,
				required: true
			},
			title: {
				type: String,
				required: true
			},
			price: {
				type: String,
				default: '面议'
			},
			soldCount: {
				type: [Number, String],
				default: 0
			},
			contactPhone: {
				type: String,
				default: ''
			},
			clickCount: {
				type: [Number, String],
				default: 0
			},
			goodsId: {
				type: [Number, String],
				default: ''
			}
		},
		computed: {
			isNegotiable() {
				return this.price === '面议'
			},
			formatClickCount() {
				const num = Number(this.clickCount)
				if (isNaN(num)) return this.clickCount
				if (num >= 10000) {
					return (num / 10000).toFixed(1) + '万'
				}
				return String(this.clickCount)
			}
		},
		methods: {
			handleCardClick() {
				if (this.goodsId) {
					this.$router.push({
						name: 'MallGoodsDetail',
						params: { id: this.goodsId }
					})
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.mall-goods-card {
		background: #fff;
		overflow: hidden;
		box-shadow: 0px 0px 10px 0px rgba(51, 51, 51, 0.2);
		border: 2px solid #ffffff;
		transition: all 0.3s ease;
		cursor: pointer;

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
		}

		// 图片容器：按 323:198 比例自适应，便于后期维护定制尺寸
		.card-image-wrap {
			position: relative;
			width: 100%;
			aspect-ratio: 323 / 198;
			overflow: hidden;
			background-color: #f5f5f5;

			.card-image {
				width: 100%;
				height: 100%;
				object-fit: cover;
				transition: transform 0.3s ease;
				display: block;
			}

			&:hover .card-image {
				transform: scale(1.05);
			}

			// 右上角点击量标签
			.click-count-tag {
				position: absolute;
				top: 0;
				right: 0;
				min-width: 64px;
				padding: 6px 10px 6px 12px;
				background-color: #eeae15;
				color: #fff;
				text-align: center;
				border-radius: 0 0 0 10px;

				.click-count-num {
					font-size: 12px;
					font-weight: bold;
					line-height: 1.2;
				}

				.click-count-label {
					font-size: 12px;
					line-height: 1.2;
					margin-top: 2px;
					opacity: 0.95;
				}
			}
		}

		.card-body {
			padding: 15px;

			.card-title {
				font-size: 16px;
				color: #333;
				font-weight: 500;
				line-height: 1.4;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}

			.card-divider {
				height: 1px;
				background-color: #eee;
				margin: 16px 0;
			}

			.card-footer {
				display: flex;
				justify-content: space-between;
				align-items: center;

				.price-section {
					.price {
						font-size: 18px;
						color: #f37920;
						font-weight: bold;

						&.negotiable {
							color: #f37920;
						}

						.price-symbol {
							font-size: 14px;
							margin-right: 1px;
						}
					}
				}

				.action-section {
					.sold-count {
						font-size: 14px;
						color: #2b6398;
					}

					.contact-trigger {
						position: relative;
						display: inline-block;

						.contact-text {
							font-size: 14px;
							color: #2b6398;
							cursor: pointer;
							transition: color 0.3s;

							&:hover {
								color: #2b6398;
							}
						}

						// 悬浮联系方式提示
						.contact-tooltip {
							position: absolute;
							bottom: calc(100% + 8px);
							left: 0;
							transform: translateX(-50%) translateY(5px);
							background: #336db1;
							color: #fff;
							padding: 5px 10px;
							font-size: 14px;
							white-space: nowrap;
							opacity: 0;
							visibility: hidden;
							transition: all 0.3s ease;
							z-index: 10;
							pointer-events: none;

							// 小三角箭头
							&::after {
								content: '';
								position: absolute;
								top: 100%;
								right: 15px;
								transform: translateX(-50%);
								border: 6px solid transparent;
								border-top-color: #336db1;
							}
						}

						&:hover .contact-tooltip {
							opacity: 1;
							visibility: visible;
							transform: translateX(-50%) translateY(0);
						}
					}
				}
			}
		}
	}
</style>
